import type {
  ContentCollection,
  ContentReferenceTypeGroup,
  ContentTypeGroup,
  LocationAttribute,
  PersonProfile,
  Tag,
} from '@cds/rbcom-content-schema'
import {
  isAppleImmersiveVideo,
  isAudioClip,
  isAudioEpisode,
  isAudioSeason,
  isAudioSeries,
  isAuthorProfile,
  isContentCollection,
  isContentReferenceTypeGroup,
  isEnergyDrink,
  isEpisodeVideo,
  isEventDetail,
  isEventProfile,
  isEventSeason,
  isEventSeries,
  isExternalContent,
  isExternalPerson,
  isExternalTeam,
  isFilm,
  isImage,
  isImageGallery,
  isImmersive3DModel,
  isJudge,
  isLiveVideo,
  isLocation,
  isMisc,
  isOccasion,
  isOccasionCampaign,
  isOnsitePromotion,
  isPartnerProfile,
  isPersonProfile,
  isPhotographer,
  isPreviewClip,
  isProjectProfile,
  isRallyDriver,
  isRallyTeam,
  isRecapVideo,
  isResultsAndStandings,
  isSeason,
  isShow,
  isStory,
  isTag,
  isTeamProfile,
  isTrailerVideo,
  isTvCommercial,
  isTvPartnerChannel,
  isVideo,
  isVideo3D,
  isVideo360,
  isVideoChannel,
} from '@cds/rbcom-content-schema'
import { parseResourceId } from '@crepo/crepo-content-api-node-sdk-cjs'

import type {
  AssetLocalizationIdentifier,
  LanguageCode,
  PropertyAssetValue,
  Space,
  StencilName,
  UUID,
} from '../models'
import {
  isFullLanguageCode,
  isSpace,
  isUuid,
  stencilNameFromString,
  stencilToContentTypeMapping,
} from '../models'
import {
  tryAddInternationalization,
  tryRemoveInternationalization,
} from './language-code.util'
import { isPartialComplete } from './object.util'

export const transformToAssetLocalizationIdentifier = (resource: Pick<ContentTypeGroup,'id'>): AssetLocalizationIdentifier => {
  const { guid, locale } = parseResourceId(resource.id)

  if (!locale || !isFullLanguageCode(locale)) {
    throw new Error(`The locale for resource '${resource.id}' could not be parsed`)
  }

  if (!isUuid(guid)) {
    throw new Error(`The uuid for resource '${resource.id}' could not be parsed`)
  }

  return {
    id: guid,
    localization: tryRemoveInternationalization(locale),
  }
}

export const contentCollectionToStencil = (
  editorial: ContentCollection['attributes']['editorial'],
  subType: ContentCollection['attributes']['subType']
): StencilName | undefined => {
  if (editorial) {
    return 'editorial-list'
  }

  if (subType === 'content-hub') {
    return 'content-hub'
  }

  if (subType === 'discipline' || subType === 'custom' || subType === 'playlist') {
    return 'content-collection'
  }

  return undefined
}

export const locationsToStencil = (precision?: LocationAttribute['precision']): StencilName | undefined => {
  switch (precision) {
    case 'address':
      return 'geolocation'
    case 'country':
      return 'country'
    case 'orb':
      return 'worldwide-geolocation'
    case 'virtual':
      return 'virtual-geolocation'
    default:
      return undefined
  }
}

export const personProfilesToStencil = (subType: PersonProfile['attributes']['subType']): StencilName | undefined => {
  switch (subType) {
    case 'artist':
      return 'artist'
    case 'athlete':
      return 'athlete'
    default:
      return undefined
  }
}

export const tagsToStencil = (subType?: Tag['attributes']['subType']): StencilName | undefined => {
  switch (subType) {
    case 'abstract':
    case 'discipline-pseudogroup':
      return 'collection'
    case 'custom':
      return 'content-category'
    case 'playground':
    case 'playground-project':
      return 'playground'
    case 'discipline':
      return subType
    default:
      return undefined
  }
}

// eslint-disable-next-line max-lines-per-function
export const resourceToStencil = (
  resource?: ContentTypeGroup
  // eslint-disable-next-line sonarjs/cognitive-complexity
): StencilName | undefined => {
  if (isAppleImmersiveVideo(resource)) {
    return 'apple-immersive-video'
  }

  if (isAudioClip(resource)) {
    return 'audio-clip'
  }

  if (isAudioEpisode(resource)) {
    return 'podcast-episode'
  }

  if (isAudioSeason(resource)) {
    return 'podcast-season'
  }

  if (isAudioSeries(resource)) {
    return 'podcast-show'
  }

  if (isAuthorProfile(resource)) {
    return 'author'
  }

  if (isContentCollection(resource)) {
    return contentCollectionToStencil(resource.attributes.editorial, resource.attributes.subType)
  }

  if (isEnergyDrink(resource)) {
    return 'energy-drink'
  }

  if (isEpisodeVideo(resource)) {
    return 'episode-video'
  }

  if (isEventDetail(resource)) {
    return 'event-details'
  }

  if (isEventProfile(resource)) {
    return 'event-profile'
  }

  if (isEventSeason(resource)) {
    return 'event-season'
  }

  if (isEventSeries(resource)) {
    return 'event-series'
  }

  if (isExternalContent(resource)) {
    return 'external-content-resource'
  }

  if (isExternalPerson(resource)) {
    return 'external-person'
  }

  if (isExternalTeam(resource)) {
    return 'external-team'
  }

  if (isFilm(resource)) {
    return 'film'
  }

  if (isImage(resource)) {
    return 'image'
  }

  if (isImageGallery(resource)) {
    return 'image-gallery'
  }

  if (isImmersive3DModel(resource)) {
    return 'immersive-3d-model'
  }

  if (isJudge(resource)) {
    return 'judge'
  }

  if (isLiveVideo(resource)) {
    return 'live-video'
  }

  if (isLocation(resource)) {
    return locationsToStencil(resource.attributes.location.precision)
  }

  if (isMisc(resource)) {
    return 'misc'
  }

  if (isOccasion(resource)) {
    return 'occasion'
  }

  if (isOccasionCampaign(resource)) {
    return 'occasion-campaign'
  }

  if (isOnsitePromotion(resource)) {
    return 'onsite-promotion'
  }

  if (isPartnerProfile(resource)) {
    return 'partner-profile'
  }

  if (isPersonProfile(resource)) {
    return personProfilesToStencil(resource.attributes.subType)
  }

  if (isPhotographer(resource)) {
    return 'photographer'
  }

  if (isPreviewClip(resource)) {
    return 'preview-clip'
  }

  if (isProjectProfile(resource)) {
    return 'project-profile'
  }

  if (isRallyDriver(resource)) {
    return 'rally-driver'
  }

  if (isRallyTeam(resource)) {
    return 'rally-team'
  }

  if (isRecapVideo(resource)) {
    return 'recap-video'
  }

  if (isResultsAndStandings(resource)) {
    return 'results-and-standings'
  }

  if (isSeason(resource)) {
    return 'season'
  }

  if (isShow(resource)) {
    return 'show'
  }

  if (isStory(resource)) {
    return 'story'
  }

  if (isTag(resource)) {
    return tagsToStencil(resource.attributes.subType)
  }

  if (isTeamProfile(resource)) {
    return 'team'
  }

  if (isTrailerVideo(resource)) {
    return 'trailer-video'
  }

  if (isTvCommercial(resource)) {
    return 'tv-commercial'
  }

  if (isTvPartnerChannel(resource)) {
    return 'tv-partner-channel'
  }

  if (isVideo(resource)) {
    return 'video'
  }

  if (isVideo3D(resource)) {
    return 'video-3d'
  }

  if (isVideo360(resource)) {
    return 'video-vr-360'
  }

  if (isVideoChannel(resource)) {
    return 'video-channel'
  }

  return undefined
}

export const crepoType = (localization: { stencil: StencilName }): ContentTypeGroup['type'] => {
  const type = stencilToContentTypeMapping[localization.stencil]

  if (type) {
    return `rrn:content:${type}`
  } else {
    throw new Error(`There is no mapping for type "${type}"`)
  }
}

export const crepoId = (localization: { id: UUID; stencil: StencilName; localization: LanguageCode }): string =>
  `${crepoType(localization)}:${localization.id}:${tryAddInternationalization(localization.localization)}`

export const masterId = (localization: { id: UUID; stencil: StencilName }): string =>
  `${crepoType(localization)}:${localization.id}`

export const crepoResourceUrl = (localization: { id: UUID; stencil: StencilName; localization: LanguageCode }): string =>
  `/v4/types/${crepoType(localization)}/resources/${crepoId(localization)}`

export const masterType = (stencil: StencilName): string => {
  return `rrn:master:${stencilToContentTypeMapping[stencil]}`
}

export const extractIdFromContentId = (contentId: string): string => {
  return parseResourceId(contentId).guid
}

export const hasType = (value: unknown): value is { type: string } =>
  typeof value === 'object' && !Array.isArray(value) && value !== undefined && value !== null && 'type' in value

export const requireIdForType = (type: string, id: string): string => {
  if (!id.startsWith(type)) {
    throw Error('invalid id')
  }

  return id
}

export const crepoResourceReference = (id: string, stencil: string): ContentReferenceTypeGroup => {
  const stencilName = stencilNameFromString(stencil)
  const type = stencilToContentTypeMapping[stencilName]

  const reference = {
    id: `rrn:content:${type}:${id}`,
    type: `rrn:master:${type}`,
  }

  if (isContentReferenceTypeGroup(reference)) {
    return reference
  } else {
    throw new Error(`There is no mapping for stencil "${stencil}"`)
  }
}

export const assetToCrepoResourceReference = (asset?: PropertyAssetValue): ContentReferenceTypeGroup | undefined => {
  if (asset) {
    const stencilName = asset.stencil
    const id = asset.id

    return stencilName ? crepoResourceReference(id, stencilName) : undefined
  }

  return undefined
}

export const crepoResourceFromPartial = (resource: Partial<ContentTypeGroup>): ContentTypeGroup => {
  if (!isPartialComplete(resource, 'id', 'type')) {
    throw new Error('The properties "id" and/or "type" are not defined')
  }

  return resource
}

export const extractSpacesFromResource = (resource: ContentTypeGroup): Space[] => {
  const spaces = resource.meta?.spaces ?? []

  if (!spaces.every(isSpace)) {
    throw new Error(`Not all spaces from the resource with id '${resource.id}' are valid. Spaces: ${spaces.join(',')}`)
  }

  return spaces
}
